import React from "react";

/**
 * MeditationChapterDivider — a wide, shallow editorial chapter marker
 * placed above each meditation category (Beginner · Intermediate ·
 * Advanced). It is deliberately quieter than the main Rituals hero:
 * shorter in height, restrained typography, and identical treatment
 * across the three chapters so the difference lives entirely in the
 * photograph itself.
 *
 * The trio functions as ONE visual journey — ARRIVE → DEEPEN → EXPAND —
 * so the page reads as a guided arc, not a long catalogue of cards.
 *
 * Design constraints
 *   • Consistent 21:8 aspect on desktop (very wide, very shallow).
 *   • Same rounded-md corner radius, same warm ink grading, same
 *     eyebrow / headline typography across all three.
 *   • No CTAs, no chrome, no icons that read as UI — the moment is
 *     photographic and typographic only.
 *
 * PROPS
 *   eyebrow   string  — "ARRIVE" / "DEEPEN" / "EXPAND"
 *   line      string  — the italic editorial line
 *   image     string  — the photograph URL
 *   alt       string  — accessible description of the photograph
 *   testid    string  — data-testid root
 */
export default function MeditationChapterDivider({ eyebrow, line, image, alt, testid }) {
  return (
    <figure
      className="relative w-full overflow-hidden rounded-md reveal"
      style={{ aspectRatio: "21 / 8" }}
      data-testid={testid}
    >
      <img
        src={image}
        alt={alt}
        loading="lazy"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ filter: "saturate(0.9) contrast(0.98)" }}
      />
      {/* Warm ink wash so the editorial type is always readable, without
          drowning the photograph. The gradient is left-weighted so the
          right side of the image can still breathe. */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(90deg, rgba(41,39,37,0.55) 0%, rgba(41,39,37,0.35) 42%, rgba(41,39,37,0.05) 100%)",
        }}
      />
      <figcaption className="absolute inset-0 flex items-center">
        <div className="pl-6 sm:pl-10 md:pl-16 max-w-2xl">
          <div
            className="text-[10px] sm:text-[11px] uppercase tracking-[0.42em] text-[hsl(var(--paper))]/95"
            data-testid={`${testid}-eyebrow`}
          >
            {eyebrow}
          </div>
          <p
            className="mt-3 sm:mt-4 font-display italic text-[hsl(var(--paper))] text-xl sm:text-2xl md:text-3xl leading-snug"
            data-testid={`${testid}-line`}
          >
            {line}
          </p>
        </div>
      </figcaption>
    </figure>
  );
}
